/**
 * Per-user preferences (P115) — the cross-device store backing onboarding
 * rail dismissals + last_role_used. Replaces the P107 localStorage util
 * as the source of truth for dismissals.
 *
 * Mirrors backend shared/me_preferences_router.py:
 *   GET   /v1/me/preferences
 *   PATCH /v1/me/preferences
 */
import { useContext } from "react";

import { PreferencesUnreachable } from "shared/api/preferencesReach";
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { api, type ApiError } from "shared/api/client";
import { userPreferencesKeys } from "shared/api/queryClient";
import type { UserPreferences } from "shared/api/types";

export function useUserPreferences(): UseQueryResult<UserPreferences, ApiError> {
  // Round 18 S4 -- not on a seat that cannot read it (the examiner's).
  const unreachable = useContext(PreferencesUnreachable);
  return useQuery({
    queryKey: userPreferencesKeys.all,
    queryFn: () => api.get<UserPreferences>("/v1/me/preferences"),
    staleTime: 5 * 60_000,
    enabled: !unreachable,
  });
}

/** Record the position the user last worked in (default-landing hint). */
export function useSetLastRole(): UseMutationResult<UserPreferences, ApiError, string> {
  const qc = useQueryClient();
  return useMutation({
    /* Round 12 F2 -- a preference write is a CONVENIENCE, and its
       failure announced itself as the reader's own work failing: "That
       did not save -- Internal server error", raised by a plain
       navigation, about a last-role write they never asked for. The
       server-side race is fixed; this is the other half, because the
       next background write that fails should not shout either. The
       reader loses a remembered filter, not a judgement. */
    meta: { handled: true },
    mutationFn: (role) =>
      api.patch<UserPreferences>("/v1/me/preferences", { last_role_used: role }),
    onSuccess: (data) => qc.setQueryData(userPreferencesKeys.all, data),
  });
}

/** Set the email-digest cadence (P134; "off" clears it). */
export function useSetDigestMode(): UseMutationResult<
  UserPreferences,
  ApiError,
  "off" | "daily" | "weekly"
> {
  const qc = useQueryClient();
  return useMutation({
    /* Round 12 F2 -- a preference write is a CONVENIENCE, and its
       failure announced itself as the reader's own work failing: "That
       did not save -- Internal server error", raised by a plain
       navigation, about a last-role write they never asked for. The
       server-side race is fixed; this is the other half, because the
       next background write that fails should not shout either. The
       reader loses a remembered filter, not a judgement. */
    meta: { handled: true },
    mutationFn: (mode) =>
      api.patch<UserPreferences>("/v1/me/preferences", { digest_mode: mode }),
    onSuccess: (data) => qc.setQueryData(userPreferencesKeys.all, data),
  });
}

/** Dismiss a rail: append (deduped) to the current set + PATCH. */
export function useDismissRail(): UseMutationResult<
  UserPreferences,
  ApiError,
  { rail: string; current: string[] }
> {
  const qc = useQueryClient();
  return useMutation({
    /* Round 12 F2 -- a preference write is a CONVENIENCE, and its
       failure announced itself as the reader's own work failing: "That
       did not save -- Internal server error", raised by a plain
       navigation, about a last-role write they never asked for. The
       server-side race is fixed; this is the other half, because the
       next background write that fails should not shout either. The
       reader loses a remembered filter, not a judgement. */
    meta: { handled: true },
    mutationFn: ({ rail, current }) => {
      const next = current.includes(rail) ? current : [...current, rail];
      return api.patch<UserPreferences>("/v1/me/preferences", {
        dismissed_rails: next,
      });
    },
    onSuccess: (data) => qc.setQueryData(userPreferencesKeys.all, data),
  });
}

/** Stitching review 5.5 -- record that this person has met a construct,
 *  so its definition is taught once. The server appends it under a row
 *  lock. A failure costs a repeated explanation, not a judgement. */
export function useMeetTerm(): UseMutationResult<UserPreferences, ApiError, string> {
  const qc = useQueryClient();
  return useMutation({
    meta: { handled: true },
    mutationFn: (term) =>
      api.patch<UserPreferences>("/v1/me/preferences", { met_term: term }),
    onSuccess: (data) => qc.setQueryData(userPreferencesKeys.all, data),
  });
}
